import React from 'react';
import styled from 'styled-components/native';
import Icon from '../../icon';
import {useTheme} from 'styled-components';
import {Theme} from '../../../theme/lightTheme';
import {useNavigation, useRoute} from '@react-navigation/native';
import {BackIconProps} from '../../../interfaces/components/header';
import {I18nManager, Platform} from 'react-native';
import screens from '../../../config/screens';

const Container = styled.View`
  transform: ${I18nManager.isRTL ? 'rotate(180deg)' : 'rotate(0deg)'};
  border-radius: 100px;
  min-width: 40px;
  min-height: 40px;
  align-items: center;
  justify-content: center;
`;
/**
 * `BackIcon` is a React component representing a back navigation icon. It can be used to handle
 * navigation actions such as going back in the navigation stack.
 *
 * @component
 *
 * @param {Object} props - The properties of the `BackIcon` component.
 * @param {() => void} [props.onPress] - A callback function to be executed when the icon is pressed.
 * @param {string} [props.iconName='arrow-back-ios-new'] - The name of the icon to be displayed.
 * @param {string} [props.iconType='MaterialIcons'] - The type of the icon (e.g., 'MaterialIcons').
 * @param {number} [props.iconSize] - The size of the icon.
 * @param {string} [props.tintColor] - The color of the icon.
 * @param {boolean} props.canGoBack - Indicates whether the icon should allow going back in the navigation stack.
 *
 * @returns {React.ReactElement} A React functional component representing the back navigation icon.
 */
const BackIcon = ({
  onPress,
  iconName,
  iconType,
  iconSize,
  tintColor,
  canGoBack,
  isGradient,
  style,
}: BackIconProps): React.ReactElement => {
  const theme = useTheme() as Theme;
  const navigation = useNavigation();
  const route = useRoute();

  /**
   * Handles the press event of the back icon. If `onPress` is provided, it will be executed;
   * otherwise, it will navigate back if `canGoBack` is `true`.
   */
  const onBackPressHandler = () => {
    try {
      if (onPress) {
        onPress();
        return;
      }
      if (canGoBack) {
        // Special handling for forgetPassword screen on web
        if (
          Platform.OS === 'web' &&
          route.name === screens.auth.forgetPassword
        ) {
          // @ts-ignore - Navigation type issue with web
          navigation.navigate(screens.auth.login);
        } else if (Platform.OS === 'web' && typeof window !== 'undefined') {
          window?.history?.back();
        } else if (navigation?.goBack) {
          navigation?.goBack();
        }
      }
    } catch (error) {
    }
  };

  // Render an empty container if navigation backward is not allowed
  if (!canGoBack) {
    return <Container style={style} />;
  }
  return (
    <Container style={style}>
      <Icon
        name={iconName || 'back'}
        color={tintColor || theme.text.dark}
        size={iconSize || theme.text.s55}
        role={'button'}
        onPress={onBackPressHandler}
      />
    </Container>
  );
};

export default BackIcon;
